import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../table/Table";
import VoiceIcon from "../../img/voice-icon.svg";
import { useSpeechSynthesis } from "react-speech-kit";
import "./SorteoView.css";

const getChar = (number) => {
  if (number < 15) return "B";
  if (number < 30) return "I";
  if (number < 45) return "N";
  if (number < 60) return "G";
  return "O";
};

const sample = (arr) => arr[Math.floor(Math.random() * arr.length)];

// 🔧 Helpers para normalizar y deduplicar winners
const normalizeGanador = (g) => ({
  serie: g?.serie ?? null,
  carton: g?.carton ?? null,
  ticketId: Number.isFinite(Number(g?.ticketId)) ? Number(g.ticketId) : null,
  posicion: Number.isFinite(Number(g?.posicion)) ? Number(g.posicion) : null,
  qr_code:
    typeof g?.qr_code === "string"
      ? g.qr_code.trim()
      : typeof g?.qr === "string"
      ? g.qr.trim()
      : "",
});

const winnerKey = (g) => {
  return g.qr_code && g.qr_code.length
    ? `qr:${g.qr_code}`
    : `serie:${g.serie}|carton:${g.carton}|id:${
        g.ticketId ?? g.posicion ?? ""
      }`;
};

const uniqueWinners = (arr) => {
  const seen = new Set();
  const out = [];
  for (const raw of arr || []) {
    const g = normalizeGanador(raw);
    const key = winnerKey(g);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(g);
    }
  }
  return out;
};

const sameWinnerSet = (a, b) => {
  const keysA = (a || []).map(winnerKey).sort();
  const keysB = (b || []).map(winnerKey).sort();
  return JSON.stringify(keysA) === JSON.stringify(keysB);
};

// Helpers para mostrar valores seguros
const safePosition = (g) => {
  const t = Number(g?.ticketId);
  if (Number.isFinite(t)) return t + 1;
  const p = Number(g?.posicion);
  if (Number.isFinite(p)) return p;
  return null;
};

const displayCarton = (g) => {
  const pos = safePosition(g);
  if (g?.carton) return g.carton;
  return pos ? `posición n° ${pos}` : "desconocido";
};

const displayQR = (g, truncarQR) => {
  const qr = g?.qr_code ?? "";
  return typeof qr === "string" && qr.trim() ? truncarQR(qr.trim()) : "sin QR";
};

const iniciarCronometro = (setBloquearSortear, setContador) => {
  setBloquearSortear(true);
  setContador(10);

  const interval = setInterval(() => {
    setContador((prev) => {
      if (prev <= 1) {
        clearInterval(interval);
        setBloquearSortear(false);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
};

const SorteoView = () => {
  const [contador, setContador] = useState(0);
  const [bloquearSortear, setBloquearSortear] = useState(false);

  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  }, [navigate]);

  const [squares, setSquares] = useState(
    Array.from(Array(75)).map((_, idx) => ({
      id: idx + 1,
      char: getChar(idx),
      state: false,
      orden: null,
    }))
  );

  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const [isButtonCloseDisable, setIsButtonCloseDisable] = useState(true);

  const [nombreSorteo, setNombreSorteo] = useState("");
  const [fechaSorteo, setFechaSorteo] = useState("");
  const [patronActual, setPatronActual] = useState(null);

  const [currentSorted, setCurrentSorted] = useState(null);
  const [ganadores, setGanadores] = useState([]);
  const [showAlerta, setShowAlerta] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [ganadoresConfirmados, setGanadoresConfirmados] = useState(new Set());

  const [tiradasList, setTiradasList] = useState([]);

  const { speak, voices } = useSpeechSynthesis();
  const API_URL = process.env.REACT_APP_API_URL;

  const winnersPrevios = useRef([]);
  const alertaMostrada = useRef(false);

  // 🔄 Resetear estado cuando cambia el patrón
  useEffect(() => {
    if (patronActual?._id && patronActual._id !== "sin_patron") {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      if (!ganadoresConfirmados.size) {
        setIsButtonDisable(false);
      }
      alertaMostrada.current = false;
      winnersPrevios.current = [];
    }
  }, [patronActual?._id]);

  // 🔄 Al montar el componente, verificar si hay winners ya confirmados
  useEffect(() => {
    if (
      !patronActual ||
      !patronActual._id ||
      patronActual._id === "sin_patron"
    ) {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      setIsButtonDisable(false);
      alertaMostrada.current = false;
      return;
    }

    const verificarConfirmaciones = async () => {
      try {
        const res = await fetch(`${API_URL}/verificar_ganador`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            nombreSorteo,
            fechaSorteo,
            patronId: patronActual._id,
          }),
        });

        if (!res.ok) return;

        const data = await res.json();
        if (data.hayGanador && Array.isArray(data.ganadores)) {
          // Filtrar solo winners con QR válido
          const winnersConQR = data.ganadores.filter(
            (w) => w?.qr_code && w.qr_code.trim().length > 0
          );

          // Si no hay winners con QR (es decir, no hay tickets vendidos), permitir sorteos
          if (winnersConQR.length === 0) {
            setGanadores([]);
            setGanadoresConfirmados(new Set());
            setShowAlerta(false);
            setCountdown(0);
            setIsButtonDisable(false);
            setIsButtonCloseDisable(true);
            alertaMostrada.current = false;
            return;
          }

          const qrConfirmados = new Set();
          for (const w of winnersConQR) {
            try {
              const checkRes = await fetch(
                `${API_URL}/participaciones/confirmado_publico?sorteo_id=${nombreSorteo}&patron_id=${patronActual._id}&carton_id=${w.qr_code}`
              );
              if (checkRes.ok) {
                const checkData = await checkRes.json();
                if (checkData.confirmado) {
                  qrConfirmados.add(w.qr_code);
                }
              }
            } catch (e) {}
          }

          if (qrConfirmados.size > 0) {
            setGanadoresConfirmados(qrConfirmados);
            setShowAlerta(false);
            setCountdown(0);
            setIsButtonDisable(true);
            setIsButtonCloseDisable(false);
            alertaMostrada.current = false;
          }
        } else {
          setGanadores([]);
          setGanadoresConfirmados(new Set());
          setShowAlerta(false);
          setCountdown(0);
          setIsButtonDisable(false);
          setIsButtonCloseDisable(true);
          alertaMostrada.current = false;
        }
      } catch (e) {
        console.log("Error al verificar confirmaciones:", e);
      }
    };

    verificarConfirmaciones();
  }, [patronActual?._id, nombreSorteo, fechaSorteo]);

  // 🔄 Resetear cuando no hay patrón activo
  useEffect(() => {
    if (!patronActual || patronActual._id === "sin_patron") {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      setIsButtonDisable(false);
      alertaMostrada.current = false;
    }
  }, [patronActual]);

  // 🔍 Verificar winner
  const verificarGanador = useCallback(async () => {
    if (!patronActual || !patronActual._id) return;
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const res = await fetch(`${API_URL}/verificar_ganador`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombreSorteo,
          fechaSorteo,
          patronId: patronActual._id,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json();

      if (data.hayGanador && Array.isArray(data.ganadores)) {
        const dedup = uniqueWinners(data.ganadores);

        // Verificar cuáles están confirmados en la DB
        const qrCodesConfirmadosEnDB = new Set();
        for (const w of dedup) {
          try {
            const checkRes = await fetch(
              `${API_URL}/participaciones/confirmado_publico?sorteo_id=${nombreSorteo}&patron_id=${patronActual._id}&carton_id=${w.qr_code}`
            );
            if (checkRes.ok) {
              const checkData = await checkRes.json();
              if (checkData.confirmado) {
                qrCodesConfirmadosEnDB.add(w.qr_code);
              }
            }
          } catch (e) {
            console.log("Error al verificar confirmación:", e);
          }
        }

        if (qrCodesConfirmadosEnDB.size > 0) {
          setGanadoresConfirmados(qrCodesConfirmadosEnDB);
        }

        if (!sameWinnerSet(dedup, winnersPrevios.current)) {
          setGanadores(dedup);
          winnersPrevios.current = dedup;
        }
      } else if (!data.hayGanador) {
        if (winnersPrevios.current.length > 0) {
          setGanadores([]);
          winnersPrevios.current = [];
        }
      }

      console.log("Ganadores (dedup):", data);
    } catch (error) {
      console.error("Error al verificar winner:", error);
    }
  }, [API_URL, nombreSorteo, fechaSorteo, patronActual, handleUnauthorized]);

  const verificarGanadorRef = useRef();

  verificarGanadorRef.current = async () => {
    if (!patronActual || !patronActual._id) return;
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const res = await fetch(`${API_URL}/verificar_ganador`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombreSorteo,
          fechaSorteo,
          patronId: patronActual._id,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json();

      if (data.hayGanador && Array.isArray(data.ganadores)) {
        const dedup = uniqueWinners(data.ganadores);
        if (!sameWinnerSet(dedup, winnersPrevios.current)) {
          setGanadores(dedup);
          winnersPrevios.current = dedup;
        }
      } else if (!data.hayGanador) {
        setGanadores([]);
        winnersPrevios.current = [];
      }
    } catch (error) {
      console.error("Error al verificar winner:", error);
    }
  };

  // 🔄 Cargar info del sorteo y tiradas previas
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resInfo = await fetch(`${API_URL}/info_sorteo_actual`);
        const info = await resInfo.json();

        setNombreSorteo(info.nombreSorteo);
        setFechaSorteo(info.fechaSorteo);

        const patronObj = info.patronActivo
          ? {
              _id: info.patronActivo,
              nombre: info.patronActivo,
              descripcion: info.descriPatron || "",
            }
          : null;

        setPatronActual(patronObj);

        if (patronObj && patronObj._id) {
          const resTiradas = await fetch(
            `${API_URL}/tiradas_sorteo/${info.nombreSorteo}/${info.fechaSorteo}/${patronObj._id}`
          );
          const tiradasData = await resTiradas.json();

          const dataOrdenada = [...tiradasData].sort(
            (b, a) => b.orden - a.orden
          );

          setTiradasList(dataOrdenada);

          if (Array.isArray(tiradasData) && tiradasData.length > 0) {
            setSquares((prev) =>
              prev.map((square) => {
                const tirada = tiradasData.find(
                  (t) => t.ficha === `${square.char}${square.id}`
                );
                return tirada
                  ? { ...square, state: true, orden: tirada.orden }
                  : square;
              })
            );

            const ultimaPorOrden = tiradasData.reduce(
              (max, t) =>
                max === null || (t?.orden ?? 0) > (max?.orden ?? 0) ? t : max,
              null
            );

            if (ultimaPorOrden) {
              setCurrentSorted({
                ficha: ultimaPorOrden.ficha,
                char: ultimaPorOrden.ficha[0],
                id: parseInt(ultimaPorOrden.ficha.slice(1), 10),
                orden: ultimaPorOrden.orden,
              });
            }
          }

          verificarGanadorRef.current();
        }
      } catch (error) {
        console.error("Error al cargar sorteo:", error);
      }
    };

    fetchData();
  }, [API_URL]);

  // 🎯 Función para sortear nueva ficha
  const sortNumber = () => {
    if (
      patronActual?._id === "sin_patron" ||
      patronActual?._id === "Sin patrón activo"
    ) {
      alert("⚠️ No se puede sortear mientras este sin patrón activo.");
      return;
    }

    // Verificar si ya hay un winner confirmado
    if (ganadoresConfirmados.size > 0) {
      alert(
        "⚠️ Ya hay un winner confirmado. Cierra las tiradas para continuar."
      );
      return;
    }

    if (!patronActual?._id) {
      console.error("No se puede guardar tirada: patronActual._id está vacío");
      return;
    }

    const unsorted = squares.filter((square) => !square.state);
    if (unsorted.length === 0) {
      alert("Todos los números han sido sorteados.");
      return;
    }

    let newSorted;
    do {
      newSorted = sample(unsorted);
    } while (newSorted && newSorted.char === currentSorted?.char);

    if (newSorted) {
      const ordenActual = Math.max(...squares.map((s) => s.orden || 0));
      const nuevaFichaObj = {
        ficha: `${newSorted.char}${newSorted.id}`,
        orden: ordenActual + 1,
      };

      fetch(`${API_URL}/last_draw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ficha: `${newSorted.char}${newSorted.id}`,
          nombreSorteo,
          fechaSorteo,
          orden: ordenActual + 1,
          idPatron: patronActual._id,
        }),
      }).catch((err) => console.error("Error al guardar tirada:", err));

      setTiradasList((prev) => [nuevaFichaObj, ...prev]);

      setSquares((prev) =>
        prev.map((square) =>
          square.id === newSorted.id
            ? { ...square, state: true, orden: ordenActual + 1 }
            : square
        )
      );

      setCurrentSorted({
        ...newSorted,
        ficha: `${newSorted.char}${newSorted.id}`,
        orden: ordenActual + 1,
      });

      speak({
        text: `${newSorted.char} ${newSorted.id}`,
        voice: voices.find((v) => v.lang === "es-ES"),
        rate: 0.9,
        pitch: 1,
        volume: 1,
      });

      verificarGanador();
    }
  };

  // 🔔 Efecto cuando hay winners - controlar alerta, countdown y botón
  useEffect(() => {
    // Filtrar solo winners con QR válido (es decir, tickets realmente vendidos)
    const winnersConQR = (ganadores || []).filter(
      (w) => w?.qr_code && w.qr_code.trim().length > 0
    );
    

    const hayWinners = winnersConQR.length > 0;
    const hayWinnersConfirmados = Array.from(ganadoresConfirmados).some((qr) =>
      winnersConQR.some((w) => w.qr_code === qr)
    );
    console.log(
      "🔍 Debug - hayWinners:",
      hayWinners,
      "hayWinnersConfirmados:",
      hayWinnersConfirmados
    );

    // SIEMPRE: Si hay al menos un winner confirmado, desactivar botón
    if (hayWinnersConfirmados) {
      console.log("🏆 Winner confirmado - DESACTIVANDO botón Sortear");
      setShowAlerta(false);
      setCountdown(0);
      setIsButtonDisable(true);
      setIsButtonCloseDisable(false);
      alertaMostrada.current = false;
      return;
    }

    // Si hay winners pero NINGUNO confirmado, mostrar alerta e iniciar countdown
    // Solo si los winners tienen QR válido (tickets vendidos)
    if (hayWinners && !hayWinnersConfirmados) {
      if (!alertaMostrada.current) {
        // Primero reproducir fanfare, luego mostrar alerta
        const fanfare = new Audio("/sounds/ganador.mp3");
        fanfare.play();

        // Después de 1.5 segundos (duración aproximada de la fanfarria), mostrar alerta
        setTimeout(() => {
          setShowAlerta(true);
          setCountdown(30); // Iniciar countdown de 30 segundos
          setIsButtonDisable(true); // Bloquear Sortear mientras hay winners sin confirmar
          setIsButtonCloseDisable(false);
          alertaMostrada.current = true;
        }, 1500);
        return;
      }
      return;
    }

    // No hay winners con QR - resetear (permitir sorteos)
    setShowAlerta(false);
    setCountdown(0);
    setIsButtonDisable(false);
    setIsButtonCloseDisable(true);
    alertaMostrada.current = false;
  }, [ganadores, ganadoresConfirmados]);

  // ⏱️ Countdown manual - solo se activa cuando se sortea
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        const nuevo = prev - 1;
        if (nuevo <= 0) {
          verificarYDesactivarGanador();
        }
        return nuevo;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // 🔴 Verificar confirmación de TODOS los winners
  const verificarYDesactivarGanador = async () => {
    if (!ganadores.length) return;

    try {
      const token = localStorage.getItem("access_token");
      let alMenosUnoConfirmado = false;
      const qrCodesConfirmados = new Set();

      for (const winnerInfo of ganadores) {
        if (!winnerInfo?.qr_code) continue;

        const checkRes = await fetch(
          `${API_URL}/participaciones/confirmado_publico?sorteo_id=${nombreSorteo}&patron_id=${patronActual?._id}&carton_id=${winnerInfo.qr_code}`
        );

        if (checkRes.ok) {
          const data = await checkRes.json();
          if (data.confirmado) {
            console.log(`✅ Usuario confirmó: ${winnerInfo.qr_code}`);
            alMenosUnoConfirmado = true;
            qrCodesConfirmados.add(winnerInfo.qr_code);
          }
        }
      }

      if (qrCodesConfirmados.size > 0) {
        setGanadoresConfirmados(qrCodesConfirmados);
      }

      if (alMenosUnoConfirmado) {
        console.log(
          "🏆 Al menos un winner confirmó → Desactivando botón Sortear"
        );
        setIsButtonDisable(true);
        return;
      }

      console.log(
        "⚠️ Ningún winner confirmó - desactivando todas las participaciones..."
      );

      const qrCodesNoConfirmados = ganadores
        .filter((w) => w?.qr_code)
        .map((w) => w.qr_code);

      for (const qrCode of qrCodesNoConfirmados) {
        try {
          await fetch(
            `${API_URL}/participaciones/desactivar?sorteo_id=${nombreSorteo}&patron_id=${patronActual?._id}&carton_id=${qrCode}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } catch (e) {
          console.error("Error al desactivar:", e);
        }
      }

      setGanadores([]);
      setShowAlerta(false);
      setCountdown(0);
      alertaMostrada.current = false;
      winnersPrevios.current = [];
      setGanadoresConfirmados(new Set());
      setIsButtonDisable(false);
    } catch (error) {
      console.error("Error al verificar winners:", error);
    }
  };

  const cerrarTiradas = async () => {
    try {
      const url = `${API_URL}/mover_tiradas_a_historial`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Error al cerrar tiradas");

      await fetch(`${API_URL}/patron_activate/${patronActual?._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activo: false }),
      });

      const sinPatronId = "sin_patron";
      console.log("PATRON DE RESPALDO:", sinPatronId);
      await fetch(`${API_URL}/patron_activate/${sinPatronId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activo: true }),
      });

      alert("✅ Tiradas cerradas y movidas al historial.");
      setIsButtonCloseDisable(true);
      setShowAlerta(false);
      setGanadores([]);
      setCurrentSorted(null);
      setSquares((prev) =>
        prev.map((s) => ({ ...s, state: false, orden: null }))
      );
      alertaMostrada.current = false;
      winnersPrevios.current = [];
      setPatronActual(null);
      setTiradasList([]);
    } catch (error) {
      console.error("Error al cerrar tiradas:", error);
    }
  };

  const truncarQR = (qr) =>
    typeof qr === "string" && qr.length > 20
      ? `${qr.slice(0, 10)}...${qr.slice(-10)}`
      : qr;

  return (
    <div className="Sorteo">
      <div className="container">
        <div className="sorter">
          <div className="sorteo-info">
            <div className="sorteo-nombre">{nombreSorteo}</div>
            <div className="sorteo-fecha">Fecha del sorteo: {fechaSorteo}</div>
          </div>

          <div className="patron-info">
            <strong>Patrón actual:</strong>{" "}
            {typeof patronActual === "string"
              ? patronActual
              : patronActual?.nombre || "Sin patrón activo"}
          </div>

          <div className="ficha-actual">
            {currentSorted
              ? `${currentSorted.ficha} (orden ${currentSorted.orden})`
              : "Aún no se ha sorteado ninguna ficha"}
          </div>

          <div className="historial-sidebar-compacto">
            <div className="historial-header-mini">Panel de Secuencia</div>
            <div className="historial-scroll-horizontal">
              <div className="flex-columns-wrapper">
                {Array.from({ length: Math.ceil(tiradasList.length / 15) }).map(
                  (_, colIndex) => (
                    <div key={colIndex} className="col-mini-bingo">
                      <ul className="list-fichas-compact">
                        {tiradasList
                          .slice(colIndex * 15, colIndex * 15 + 15)
                          .map((t) => (
                            <li key={t.orden} className="item-ficha-mini">
                              <span
                                className={`circle-mini ${
                                  t.ficha.startsWith("B") ||
                                  t.ficha.startsWith("G")
                                    ? "bg-v"
                                    : "bg-r"
                                }`}
                              >
                                {t.ficha[0]}
                              </span>
                              <span className="text-ficha-mini">{t.ficha}</span>
                              <span className="text-orden-mini">
                                #{t.orden}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          <button
            className={`btn ${
              bloquearSortear || isButtonDisable ? "disabled-button" : ""
            }`}
            onClick={() => {
              if (!bloquearSortear && !isButtonDisable) {
                sortNumber();
                iniciarCronometro(setBloquearSortear, setContador);
              }
            }}
            disabled={bloquearSortear || isButtonDisable}
          >
            {bloquearSortear ? `Espera ${contador}s` : "Sortear"}
          </button>

          {bloquearSortear && (
            <div style={{ marginTop: "8px", color: "red" }}>
              Espera {contador} segundos para volver a sortear
            </div>
          )}

          {isButtonDisable && !bloquearSortear && (
            <div
              style={{ marginTop: "8px", color: "purple", fontWeight: "bold" }}
            >
              🎉 Sorteo terminado - Un ganador ha confirmado
            </div>
          )}

          <br />

          <button
            className={`btn ${isButtonDisable ? "disabled-button" : ""}`}
            onClick={() =>
              speak({
                text: `${currentSorted?.char} ${currentSorted?.id}`,
                voice: voices.find((v) => v.lang === "es-ES"),
                rate: 0.9,
                pitch: 1,
                volume: 1,
              })
            }
            disabled={isButtonDisable || !currentSorted}
          >
            <img src={VoiceIcon} alt="Leer número" />
          </button>

          {showAlerta &&
            (() => {
              // Filtrar solo winners con QR válido para mostrar en la alerta
              const winnersParaMostrar = (ganadores || []).filter(
                (w) => w?.qr_code && w.qr_code.trim().length > 0
              );
              if (winnersParaMostrar.length === 0) return null;

              return (
                <div className="alerta-ganador">
                  🎉 ¡
                  {winnersParaMostrar.length === 1 ? "Ganador" : "Ganadores"}{" "}
                  detectado
                  {winnersParaMostrar.length === 1 ? "" : "s"}!
                  <div className="countdown-timer">⏱️ Espera: {countdown}s</div>
                  <ul>
                    {winnersParaMostrar.map((g, idx) => (
                      <li key={idx}>
                        Serie: {g?.serie ?? "—"} | Cartón: {displayCarton(g)} |
                        QR: {displayQR(g, truncarQR)}
                        {ganadoresConfirmados.has(g.qr_code) ? (
                          <span
                            style={{
                              marginLeft: "10px",
                              color: "green",
                              fontWeight: "bold",
                            }}
                          >
                            ✅ Confirmado
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

          {ganadores.length > 0 && (
            <button
              className="btn cerrar-tiradas"
              onClick={cerrarTiradas}
              disabled={isButtonCloseDisable}
            >
              Cerrar tiradas
            </button>
          )}
        </div>

        <Table squares={squares} />
      </div>
    </div>
  );
};

export default SorteoView;
